import React from 'react';
import { FileTextIcon, GraduationCapIcon } from 'lucide-react';
import { CycleMark } from './CycleMark';
const blocks = [{
  time: '15 min',
  name: 'First-15 Triage',
  body: 'Identify the machine, ask what changed, read the bin — all before the toolbag opens.',
  dot: 'bg-paper'
}, {
  time: 'Step 1',
  name: 'The Split: freeze or harvest?',
  body: 'Watch and time one full cycle. One question answered before any tool comes out.',
  dot: 'bg-sky'
}, {
  time: 'Step 2',
  name: 'The 4-System Map',
  body: 'Place the fault in Water, Refrigeration, Harvest or Controls — then follow that flowchart.',
  dot: 'bg-ember'
}, {
  time: 'Step 3',
  name: 'Prove It Twice',
  body: 'Two independent tests before any part leaves the van. Signal, load, path.',
  dot: 'bg-moss'
}, {
  time: 'Close-out',
  name: 'Record it',
  body: 'Log it on the right worksheet, so the report, the warranty claim and the next visit are already done.',
  dot: 'bg-sys-refrig'
}];
export function ProductReveal() {
  return <section aria-labelledby="mechanism-heading" className="grid-dark relative w-full overflow-hidden border-y-4 border-ember bg-forest" data-id="element-272" data-mp="1f2j56ur951or">
      <CycleMark className="pointer-events-none absolute -left-20 bottom-0 h-72 w-72 text-sky/[0.08]" data-id="element-273" data-mp="1jtsutvcsw50n" />
      <div className="relative mx-auto max-w-5xl px-5 py-14 sm:py-20" data-id="element-274" data-mp="1oruf2jlyqadp">
        <div className="text-center" data-id="element-275" data-mp="s2uzskmyo8c0">
          <p className="eyebrow text-sky" data-id="element-276" data-mp="s3whv92e9v38">The mechanism</p>
          <h2 id="mechanism-heading" className="rule-underline mt-3 font-display text-[34px] uppercase leading-tight text-paper sm:text-6xl" data-id="element-277" data-mp="w2wxyjthmjmj">
            The Freeze-or-Harvest Split
          </h2>
          <p className="mono-note mt-5 text-xl leading-tight text-ember sm:text-2xl" data-id="element-278" data-mp="1635ujyre672g">
            Split first. Then test. Don’t guess.
          </p>
          <p className="mx-auto mt-3 max-w-2xl text-base text-paper/80 sm:text-lg" data-id="element-279" data-mp="2hgi2olu6pbuf">
            Most ice machine faults show up in either the freeze cycle or the
            harvest cycle. Every flowchart in the pack starts by deciding which
            one, then isolates which of the 4 systems failed — and only then do
            you measure and test the part. Leaks, dispensers and bin controls
            get their own entry door.
          </p>
        </div>

        <ol className="relative mx-auto mt-9 max-w-2xl" data-id="element-280" data-mp="1wpw4ix41ioct">
          <span aria-hidden="true" className="absolute bottom-6 left-[19px] top-6 border-l-2 border-dashed border-paper/25" data-id="element-281" data-mp="193fprblcqmu7" />
          {blocks.map((block, i) => <li key={block.name} className="relative flex gap-4 pb-4 last:pb-0" data-id="element-282" data-mp="1it90kado6xxe" data-mpi={i}>
              <span className={`relative z-10 flex h-10 w-10 flex-none items-center justify-center rounded-full font-mono text-sm font-bold text-pine ring-4 ring-forest ${block.dot} ${block.dot === 'bg-sys-refrig' ? 'text-paper' : ''}`} data-id="element-283" data-mp="1e17iu8xqgezz">
                {i + 1}
              </span>
              <div className="flex-1 rounded-xl border border-paper/10 bg-paper/[0.06] px-4 py-3" data-id="element-284" data-mp="2wz42ylqqqk6j">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3" data-id="element-285" data-mp="31rx6j6ah3mh1">
                  <span className="font-display text-xl uppercase text-paper" data-id="element-286" data-mp="2n2r3f78ilc3v">
                    {block.name}
                  </span>
                  <span className="font-mono text-[11px] font-bold uppercase tracking-widest text-ember" data-id="element-287" data-mp="2s1c0wywdthom">
                    {block.time}
                  </span>
                </div>
                <p className="mt-1 text-sm leading-snug text-paper/80" data-id="element-288" data-mp="3kp7wg0a7t3jt">
                  {block.body}
                </p>
              </div>
            </li>)}
        </ol>

        <div className="mx-auto mt-6 grid max-w-2xl gap-3 sm:grid-cols-2" data-id="element-289" data-mp="3aqiqn5vlf9jx">
          <div className="rounded-xl bg-moss-soft px-4 py-4 text-pine" data-id="element-290" data-mp="1vaa5r8pxz2vx">
            <span className="flex items-center gap-2 font-display text-lg uppercase" data-id="element-291" data-mp="1vd61qri8bhhq">
              <FileTextIcon className="h-5 w-5 text-moss-deep" aria-hidden="true" data-id="element-292" data-mp="1zaaj0q1b181e" />
              One-page Field Procedure Cards
            </span>
            <p className="mt-1.5 text-sm leading-snug text-pine-soft" data-id="element-293" data-mp="2asjgrv716bm4">
              124 cards with the same layout: use it when, tools, safety,
              numbered steps, what normal looks like, the most common mistake
              and when to escalate.
            </p>
          </div>
          <div className="rounded-xl bg-sky-soft px-4 py-4 text-pine" data-id="element-294" data-mp="2yixb5sd38mjv">
            <span className="flex items-center gap-2 font-display text-lg uppercase" data-id="element-295" data-mp="2yg1e0692e1wh">
              <GraduationCapIcon className="h-5 w-5 text-sky-deep" aria-hidden="true" data-id="element-296" data-mp="2th7y676b1yt5" />
              Apprentice / Lead-tech
            </span>
            <p className="mt-1.5 text-sm leading-snug text-pine-soft" data-id="element-297" data-mp="2kj5utkz3mu2p">
              Every card has an apprentice shortcut and a lead-tech check, so
              new and senior techs work from the same page.
            </p>
          </div>
        </div>
      </div>
    </section>;
}